import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {gsap, TimelineLite} from "gsap";
import {DumpsterFire} from "../DumpsterFire";
import {DumpsterFireGameAssets} from "../DumpsterFireGameAssets";
import {DumpsterFireSound} from "../sound/DumpsterFireSound";
import {StageLayer} from "@nolimitcity/slot-keypad/bin/core/app/StageLayer";
import {ContinueButton} from "@nolimitcity/slot-keypad/bin/core/app/gui/buttons/concretebuttons/ContinueButton";
import {ScreenBounds} from "@nolimitcity/slot-keypad/bin/core/app/display/ScreenBounds";
import {NolimitApplication} from "@nolimitcity/slot-keypad/bin/core/app/NolimitApplication";
import {SlotKeypad} from "@nolimitcity/slot-keypad/bin/SlotKeypad";

export class DFWinCapView extends PIXI.Container implements StageLayer {
    private _button: ContinueButton;
    private _clickCallback: Function;
    private _countUp: BasicBitmapCountUp;
    private _winTimes: PIXI.BitmapText;
    private _overlay: PIXI.spine.Spine;

    constructor() {
        super();
        this.name = "DFWinCapView";
        this.init();
        this.addChild(this._button);
    }

    public show(callback: Function): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._clickCallback = callback;
        this._countUp.alpha = 0;
        this._countUp.visible = true;

        this._overlay.alpha = 0;
        this._overlay.visible = true;
        const isUSPAServerAndJuris: boolean = ((!!(SlotKeypad.apiPlugIn.options && SlotKeypad.apiPlugIn.options.game && SlotKeypad.apiPlugIn.options.game.indexOf("PA") > -1)) && DumpsterFire.apiPlugin.gameClientConfiguration.jurisdictionName === "US-PA");

        tl.add([
            (): void => {
                DumpsterFire.soundPlayer.switchMusic("CAP_WIN_START");
                let startAnim: string = (DumpsterFire.apiPlugin.gameClientConfiguration.useCensoredGfxUS || DumpsterFire.apiPlugin.gameClientConfiguration.jurisdictionName === "US-NJ") || (isUSPAServerAndJuris) ? "wincap_start_US" : "wincap_start";
                this._overlay.state.setAnimation(0, startAnim, false);
                DumpsterFire.allReelsView.removeAllMulti()
                this._countUp.alpha = 1;
                this._overlay.alpha = 1;
            },
            this._countUp.getCountAnimation(1, (DumpsterFire.model.gameData.accumulatedRoundWin), 3.6, "Power4.easeInOut")]);
        let loopAnim: string = (DumpsterFire.apiPlugin.gameClientConfiguration.useCensoredGfxUS || DumpsterFire.apiPlugin.gameClientConfiguration.jurisdictionName === "US-NJ") || (isUSPAServerAndJuris) ? "wincap_loop_US" : "wincap_loop";
        tl.add((): void => {
            this._overlay.autoUpdate = true;
            this._overlay.state.setAnimation(0, loopAnim, true);
        }, 4);
        tl.to(this._countUp, {duration: 0.2, alpha: 0}, 4.2);
        tl.to([this._button, this._winTimes], {
            duration: 0.2, alpha: 1, onStart: () => {
                DumpsterFire.soundPlayer.playSound(DumpsterFireSound.CAP_WIN_MUSIC, Number.MAX_VALUE);
                this._countUp.visible = false;
                this._button.visible = true;
                this._winTimes.visible = true;
            }, onComplete: () => {
                this._button.enable(true);
            }
        }, 4.3);
        return tl;
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
            this._countUp.position.set(360, 450);
        } else {
            const centerBottom = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
            this._countUp.position.set(360, 450);
        }
    }

    private init(): void {
        this._overlay = new PIXI.spine.Spine(DumpsterFire.assetLoader.getSpineAsset("winCap"));
        this._overlay.alpha = 0;
        this._overlay.position.set(360, 360);
        this.addChild(this._overlay);

        this._button = new ContinueButton();
        this._button.addClickCallback(() => this.onButtonClick());
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.position.set(360, 630);
        this._button.visible = false;
        this._button.enable(false);

        this._winTimes = new PIXI.BitmapText(`${DumpsterFire.gameInfoData.maxMultiplier}x`, {
            fontName: DumpsterFireGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 55
        });
        this._winTimes.anchor = new PIXI.Point(0.5, 0.5);
        this._winTimes.position.set(360, 420);
        this._winTimes.visible = false;
        this.addChild(this._winTimes);

        this._countUp = new BasicBitmapCountUp({
            fontName: DumpsterFireGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 45,
        }, 580, new PIXI.Point(0.5, 0.5));
        this._countUp.visible = false;
        this.addChild(this._countUp);
        this.onResize();
    }

    private onButtonClick(): void {
        this._button.enable(false);
        DumpsterFire.backgroundElements.resetIdleTimeline();
        const hideTL: TimelineLite = new TimelineLite();
        hideTL.add(() => {
            DumpsterFire.soundPlayer.playSound(DumpsterFireSound.CAP_WIN_ENDS);
            DumpsterFire.soundPlayer.stopSound(DumpsterFireSound.CAP_WIN_MUSIC);
            DumpsterFire.buyFeatureName = "";
            DumpsterFire.model.boostedBetName = "";
            DumpsterFire.model.isFreespin = false;
            DumpsterFire.backgroundElements.setMode(DumpsterFire.model.gameData.nextMode);
            DumpsterFire.gameLogo.setMode(DumpsterFire.model.gameData.nextMode);
            DumpsterFire.winView.playNormalAnimationForAll();
            this._overlay.state.clearTracks();
            this._overlay.skeleton.setSlotsToSetupPose();
            this._overlay.skeleton.setToSetupPose();
            this._overlay.autoUpdateTransform();
        }, 0);
        hideTL.add([
            gsap.to([this._button, this._winTimes], {
                duration: 0.1, alpha: 0
            }),
            DumpsterFire.keypadPlugin.show()
        ], 0);
        hideTL.add([
            gsap.to([this._overlay], {
                duration: 0.2, alpha: 0, onComplete: () => {
                    this._overlay.visible = false;
                }
            })
        ], 0);
        hideTL.add(() => {
            DumpsterFire.soundPlayer.switchMusic(DumpsterFire.model.data.nextMode);
            this._button.visible = false;
            this._clickCallback();
        });
    }
}